import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { phoneEndpoint } from "../utils/trustCallApi";
import type { TrustCallPhone, FtcComplaint } from "../types";

interface FtcComplaintsModalProps {
  show: boolean;
  phone: TrustCallPhone | null;
  filterDays: string;
  onClose: () => void;
}

// Column definitions matching the MVC gijgo grid exactly (Monitor.cshtml lines 6058-6076)
const COLUMNS: {
  field: keyof FtcComplaint;
  title: string;
  sortable: boolean;
}[] = [
  { field: "phone", title: "Phone #", sortable: false },
  { field: "createdDate", title: "Created Date", sortable: true },
  { field: "violationDate", title: "Violation Date", sortable: true },
  { field: "city", title: "City", sortable: true },
  { field: "state", title: "State", sortable: true },
  { field: "areaCode", title: "Area Code", sortable: true },
  { field: "subject", title: "Subject", sortable: true },
  { field: "recordedMessageOrRobocall", title: "Recorded Message Or Robocall", sortable: false },
];

const FtcComplaintsModal: React.FC<FtcComplaintsModalProps> = ({
  show,
  phone,
  filterDays,
  onClose,
}) => {
  const phoneNumber = phone?.phoneNumber ?? "";
  // Use phoneNumberClean for the API call if available, fall back to stripping non-digits
  const phoneNumberForApi = phone?.phoneNumberClean || phoneNumber.replace(/\D/g, "");

  const [sortField, setSortField] = useState<keyof FtcComplaint | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const complaintsQuery = useQuery<FtcComplaint[]>({
    queryKey: ["trustcall", "ftc-complaints", phoneNumberForApi, filterDays],
    queryFn: () =>
      callApi(
        `${phoneEndpoint(phoneNumberForApi, "ftc-complaints")}?filterDays=${filterDays}`,
        HttpMethod.GET
      ),
    enabled: show && !!phoneNumberForApi,
  });

  const sortedData = useMemo(() => {
    const data = complaintsQuery.data ?? [];
    if (!sortField) return data;
    const isDateField = sortField === "createdDate" || sortField === "violationDate";
    return [...data].sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      const cmp = isDateField
        ? new Date(String(aVal)).getTime() - new Date(String(bVal)).getTime()
        : String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [complaintsQuery.data, sortField, sortDir]);

  const handleSort = (field: keyof FtcComplaint, sortable: boolean) => {
    if (!sortable) return;
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  if (!show || !phone) return null;

  return (
    <div
      className="modal fade show d-block bs-example-modal-lg"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="ftcComplaintsModalLabel"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id="ftcComplaintsModalLabel">
              Federal Trade Commission Reported Complaints
            </h3>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
          <div className="modal-body">
            <div className="row" style={{ marginBottom: 5 }}>
              <div className="col-md-12">
                <span>
                  Listed below is FTC Complaint data for phone #{" "}
                  <b>{phoneNumber}</b>
                </span>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                {complaintsQuery.isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span className="ms-2">Loading complaints...</span>
                  </div>
                ) : (
                  <table className="table table-bordered" style={{ fontSize: "12px" }}>
                    <thead>
                      <tr>
                        {COLUMNS.map((col) => (
                          <th
                            key={col.field}
                            onClick={() => handleSort(col.field, col.sortable)}
                            style={{
                              cursor: col.sortable ? "pointer" : "default",
                              userSelect: "none",
                            }}
                          >
                            {col.title}
                            {col.sortable && sortField === col.field && (
                              <span className="ms-1">
                                {sortDir === "asc" ? "\u25B2" : "\u25BC"}
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.length === 0 ? (
                        <tr>
                          <td colSpan={COLUMNS.length} className="text-center text-muted">
                            No records found
                          </td>
                        </tr>
                      ) : (
                        sortedData.map((row, idx) => (
                          <tr key={row.ftcComplaintId || idx}>
                            {COLUMNS.map((col) => (
                              <td key={col.field}>{row[col.field]}</td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FtcComplaintsModal;
